import { useEffect, useMemo, useState } from "react";
import { Calendar, ChevronDown, Clock, GripVertical, LayoutGrid, List, Plus, SlidersHorizontal } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { EntityKind } from "./entity-form";
import type { Task, TaskStatus } from "../types";
import { todayIso } from "../lib/date";
import { EmptyState, PagedList, PRIO, RowActions, Tile, fullDate } from "./bento-ui";
import { TaskDetail } from "./task-detail";
import type { SubTask } from "./task-detail";
import { isRecurrenceParent } from "./recurring-task-derive";
import { parseRrule } from "../lib/recurrence";
import { TasksFilterBar, TASK_FILTERS_DEFAULT } from "./tasks-filter-bar";
import type { TaskFilters } from "./tasks-filter-bar";

type OpenEntity = (kind: string, id: string) => void;

/** Returns a short Vietnamese cadence label for a task recurrence RRULE, e.g. "Hàng ngày". */
function recurrenceLabel(rrule: string): string {
  const r = parseRrule(rrule);
  if (!r) return "Lặp lại";
  const intervalStr = r.interval > 1 ? ` mỗi ${r.interval} ` : " ";
  switch (r.freq) {
    case "DAILY": return `Lặp${intervalStr}ngày`;
    case "WEEKLY": return `Lặp${intervalStr}tuần`;
    case "MONTHLY": return `Lặp${intervalStr}tháng`;
    default: return "Lặp lại";
  }
}

/** Badge shown on series-parent tasks. */
function RecurrenceBadge({ task }: { task: Task }) {
  if (!isRecurrenceParent(task)) return null;
  return (
    <span className="hidden shrink-0 rounded-full bg-violet-100 px-1.5 text-[10px] font-semibold text-violet-600 sm:inline">
      🔁 {recurrenceLabel(task.recurrence!)}
    </span>
  );
}

type Props = {
  api: LifeOsApi;
  onForm: (kind: EntityKind, item?: Record<string, unknown>) => void;
  onDelete: (fn: () => void) => void;
  openEntity: OpenEntity;
  focusId?: string;
};

const COLS: Array<[TaskStatus, string, string]> = [
  ["todo", "Chưa làm", "#64748b"],
  ["doing", "Đang làm", "#3b82f6"],
  ["done", "Xong", "#10b981"],
];

export function TasksView({ api, onForm, onDelete, openEntity, focusId }: Props) {
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState<"list" | "kanban">("list");
  const [over, setOver] = useState<TaskStatus | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [filters, setFilters] = useState<TaskFilters>(TASK_FILTERS_DEFAULT);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount =
    (filters.priority !== "all" ? 1 : 0) +
    (filters.tag !== "Tất cả" ? 1 : 0) +
    (filters.project !== "" ? 1 : 0) +
    (filters.showDeferred ? 1 : 0);

  useEffect(() => { if (focusId) setOpenId(focusId); }, [focusId]);

  const openTask = api.state.tasks.find((t) => t.id === openId) ?? null;

  const add = () => {
    if (title.trim()) {
      api.actions.addTask({ title: title.trim(), dueDate: todayIso(), status: "todo", priority: "medium", estimateMinutes: 30, subtasks: [] });
      setTitle("");
    }
  };

  const drop = (status: TaskStatus) => (e: React.DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (id) api.actions.updateTask(id, { status });
    setOver(null);
  };

  const today = todayIso();

  // Derive available filter options from the full task list (not filtered).
  const availableTags = useMemo(
    () => Array.from(new Set(api.state.tasks.flatMap((t) => t.tags ?? []))),
    [api.state.tasks],
  );

  const availableProjects = useMemo(() => {
    const projectIds = new Set(api.state.tasks.map((t) => t.projectId).filter(Boolean) as string[]);
    return api.state.projects
      .filter((p) => projectIds.has(p.id))
      .map((p) => ({ id: p.id, name: p.name }));
  }, [api.state.tasks, api.state.projects]);

  // Count deferred tasks (deferDate strictly in the future) among non-done tasks.
  const deferredCount = useMemo(
    () => api.state.tasks.filter(
      (t) => t.status !== "done" && !!t.deferDate && t.deferDate > today,
    ).length,
    [api.state.tasks, today],
  );

  const shared = {
    api,
    onForm,
    onDelete,
    openEntity,
    onOpen: (id: string) => setOpenId(id),
    filters,
    today,
  };

  return (
    <>
      {/* Quick-add bar + view mode toggle */}
      <Tile className="mb-4 !p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            className="min-w-0 flex-1 basis-40 rounded-xl bg-ink-200/40 px-4 py-2.5 text-[14px] outline-none"
            placeholder="Thêm việc nhanh… (Enter)"
          />
          <button
            onClick={() => onForm("task")}
            className="gbrand flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-[13px] font-bold text-white"
          >
            <Plus className="h-4 w-4" />Thêm việc
          </button>
          <div className="flex overflow-hidden rounded-xl border border-ink-200">
            <button
              onClick={() => setMode("list")}
              className={`flex items-center gap-1 px-3 py-2.5 text-[12px] font-bold ${mode === "list" ? "bg-brand-100 text-brand-600" : "text-ink-500"}`}
            >
              <List className="h-4 w-4" />Danh sách
            </button>
            <button
              onClick={() => setMode("kanban")}
              className={`flex items-center gap-1 px-3 py-2.5 text-[12px] font-bold ${mode === "kanban" ? "bg-brand-100 text-brand-600" : "text-ink-500"}`}
            >
              <LayoutGrid className="h-4 w-4" />Kanban
            </button>
          </div>
        </div>
      </Tile>

      {/* Bộ lọc: ẩn sau nút, mở khi cần (gọn gàng) — badge = số bộ lọc đang bật */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            className={`flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[13px] font-bold transition ${
              activeFilterCount > 0
                ? "border-brand-200 bg-brand-50 text-brand-600"
                : "border-ink-200 bg-white text-ink-600"
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Bộ lọc
            {activeFilterCount > 0 && (
              <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-brand-500 px-1 text-[11px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown className={`h-3.5 w-3.5 transition ${filtersOpen ? "rotate-180" : ""}`} />
          </button>
          {activeFilterCount > 0 && (
            <button
              onClick={() => setFilters(TASK_FILTERS_DEFAULT)}
              className="text-[12px] font-semibold text-ink-500 transition hover:text-rose-500"
            >
              Xóa lọc
            </button>
          )}
        </div>
        {filtersOpen && (
          <div className="mt-3">
            <TasksFilterBar
              filters={filters}
              onChange={setFilters}
              availableTags={availableTags}
              availableProjects={availableProjects}
              deferredCount={deferredCount}
            />
          </div>
        )}
      </div>

      {mode === "list"
        ? <ListMode {...shared} />
        : <KanbanMode {...shared} over={over} setOver={setOver} drop={drop} />}

      {openTask && (
        <TaskDetail
          task={openTask}
          api={api}
          openEntity={openEntity}
          onClose={() => setOpenId(null)}
          onDelete={() => onDelete(() => api.actions.remove("tasks", openTask.id))}
        />
      )}
    </>
  );
}

// ── Shared props ───────────────────────────────────────────────────────────────

type SharedProps = {
  api: LifeOsApi;
  onForm: Props["onForm"];
  onDelete: Props["onDelete"];
  openEntity: OpenEntity;
  onOpen: (id: string) => void;
  filters: TaskFilters;
  today: string;
};

// ── applyTaskFilters ───────────────────────────────────────────────────────────

/**
 * Pure filter function — applies all active filter chips to a task array.
 * Deferred tasks (deferDate > today) are hidden unless filters.showDeferred is true.
 * Priority / tag / project chips combine AND-across-categories, OR-within-category
 * (each category only has one active value, so within = single exact match).
 */
function applyTaskFilters(tasks: Task[], filters: TaskFilters, today: string): Task[] {
  return tasks.filter((t) => {
    // Defer-hide: skip future-deferred non-done tasks unless toggle is on.
    if (!filters.showDeferred && t.status !== "done" && !!t.deferDate && t.deferDate > today) {
      return false;
    }
    // Priority filter (AND).
    if (filters.priority !== "all" && t.priority !== filters.priority) return false;
    // Tag filter (AND).
    if (filters.tag !== "Tất cả" && !(t.tags ?? []).includes(filters.tag)) return false;
    // Project filter (AND).
    if (filters.project !== "" && t.projectId !== filters.project) return false;
    return true;
  });
}

// ── ListMode ───────────────────────────────────────────────────────────────────

function ListMode({ api, onOpen, openEntity, onDelete, filters, today }: SharedProps) {
  const groups = useMemo((): Array<[string, Task[], string]> => {
    const open = api.state.tasks.filter((t) => t.status !== "done");
    const done = api.state.tasks.filter((t) => t.status === "done");
    // Apply filters to each group independently.
    const filteredOpen = applyTaskFilters(open, filters, today);
    const filteredDone = applyTaskFilters(done, filters, today);
    return [
      ["Quá hạn",  filteredOpen.filter((t) => t.dueDate < today), "#f43f5e"],
      ["Hôm nay",  filteredOpen.filter((t) => t.dueDate === today), "#7c3aed"],
      ["Sắp tới",  filteredOpen.filter((t) => t.dueDate > today), "#3b82f6"],
      ["Đã xong",  filteredDone, "#10b981"],
    ];
  }, [api.state.tasks, filters, today]);

  const totalVisible = groups.reduce((n, [, items]) => n + items.length, 0);

  if (api.state.tasks.length === 0) {
    return (
      <EmptyState
        title="Chưa có việc nào"
        hint={'Gõ vào ô trên rồi Enter để thêm nhanh, hoặc bấm "+ Chi tiết" để tạo việc có hạn & ưu tiên.'}
      />
    );
  }
  if (totalVisible === 0) {
    return (
      <EmptyState
        title="Không có việc khớp bộ lọc"
        hint="Thử thay đổi bộ lọc ưu tiên, thẻ, hoặc bật hiện việc hoãn."
      />
    );
  }

  return (
    <div className="space-y-5">
      {groups.map(([label, items, color]) => items.length === 0 ? null : (
        <div key={label}>
          <div className="mb-2 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
            <h3 className="font-display text-[15px] font-bold">{label}</h3>
            <span className="text-[12px] text-ink-500">{items.length}</span>
          </div>
          <Tile className="!p-2">
            <div className="divide-y divide-ink-200/60">
              {/* Perf: nhóm lớn (vd "Đã xong" sau vài tháng) chỉ render 50 dòng đầu */}
              <PagedList items={items} pageSize={50}>
                {(t) => (
                  <div key={t.id} className="group flex items-center gap-3 px-2 py-2.5">
                    <button
                      onClick={() => api.actions.updateTask(t.id, { status: t.status === "done" ? "todo" : "done" })}
                      className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${t.status === "done" ? "border-emerald-500 bg-emerald-500 text-white" : "border-ink-300"}`}
                    >
                      {t.status === "done" && <span className="text-[11px] text-white">✓</span>}
                    </button>
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PRIO[t.priority] }} />
                    <button
                      onClick={() => onOpen(t.id)}
                      className={`min-w-0 flex-1 truncate text-left text-[14px] font-semibold ${t.status === "done" ? "text-ink-500 line-through" : ""}`}
                    >
                      {t.title}
                    </button>
                    {(t.tags ?? []).slice(0, 3).map((tag) => (
                      <span key={tag} className="hidden shrink-0 rounded-full bg-ink-200/70 px-1.5 text-[10px] font-semibold text-ink-500 md:inline">
                        #{tag}
                      </span>
                    ))}
                    {(t.tags?.length ?? 0) > 3 && (
                      <span className="hidden shrink-0 text-[10px] font-semibold text-ink-400 md:inline" title={(t.tags ?? []).slice(3).map((x) => `#${x}`).join(" ")}>
                        +{(t.tags!.length) - 3}
                      </span>
                    )}
                    <RecurrenceBadge task={t} />
                    {t.deferDate && t.deferDate > today && (
                      <span className="hidden shrink-0 rounded bg-sky-100 px-1.5 text-[10px] font-semibold text-sky-600 sm:inline">
                        ⏳ {fullDate(t.deferDate)}
                      </span>
                    )}
                    <span className="hidden text-[11px] text-ink-500 sm:block">{fullDate(t.dueDate)}</span>
                    {t.projectId && (
                      <button
                        onClick={() => openEntity("project", t.projectId!)}
                        className="hidden rounded bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-200 sm:block"
                      >
                        {api.state.projects.find((p) => p.id === t.projectId)?.name}
                      </button>
                    )}
                    {t.goalId && (() => {
                      const g = api.state.goals.find((g) => g.id === t.goalId);
                      return g
                        ? <button onClick={() => openEntity("goal", g.id)} className="hidden rounded bg-teal-100 px-1.5 text-[11px] font-semibold text-teal-700 hover:bg-teal-200 sm:block">{g.title}</button>
                        : null;
                    })()}
                    <RowActions onEdit={() => onOpen(t.id)} onDelete={() => onDelete(() => api.actions.remove("tasks", t.id))} />
                  </div>
                )}
              </PagedList>
            </div>
          </Tile>
        </div>
      ))}
    </div>
  );
}

// ── KanbanMode ─────────────────────────────────────────────────────────────────

function KanbanMode({
  api, onForm, onDelete, openEntity, onOpen, filters, today,
  over, setOver, drop,
}: SharedProps & {
  over: TaskStatus | null;
  setOver: (s: TaskStatus | null) => void;
  drop: (s: TaskStatus) => (e: React.DragEvent) => void;
}) {
  return (
    <>
      <p className="mb-2 px-1 text-[12px] text-ink-500">
        Kéo thẻ giữa các cột — hoặc bấm tên cột ở mỗi thẻ để chuyển trạng thái.
      </p>
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-3">
        {COLS.map(([status, label, color]) => {
          const raw = api.state.tasks.filter((t) => t.status === status);
          const items = applyTaskFilters(raw, filters, today);
          return (
            <div
              key={status}
              onDragOver={(e) => { e.preventDefault(); setOver(status); }}
              onDragLeave={() => setOver(over === status ? null : over)}
              onDrop={drop(status)}
              className={`rounded-3xl border-2 p-4 transition ${over === status ? "border-brand-500 bg-brand-50" : "border-transparent bg-white"}`}
            >
              <div className="mb-3 flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
                <span className="font-display text-[15px] font-bold">{label}</span>
                <span className="rounded-full bg-ink-200/60 px-2 text-[11px] font-bold text-ink-500">{items.length}</span>
              </div>
              <div className="min-h-[40px] space-y-2.5">
                {items.map((t) => (
                  <KanbanCard
                    key={t.id}
                    task={t}
                    api={api}
                    onForm={onForm}
                    onDelete={onDelete}
                    openEntity={openEntity}
                    onOpen={onOpen}
                    subCount={t.subtasks}
                  />
                ))}
                {items.length === 0 && (
                  <p className="rounded-2xl border border-dashed border-ink-300 py-8 text-center text-[12px] text-ink-500">
                    Thả thẻ vào đây
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── KanbanCard ─────────────────────────────────────────────────────────────────

function KanbanCard({
  task, api, onDelete, openEntity, onOpen, subCount,
}: {
  task: Task;
  api: LifeOsApi;
  onForm: Props["onForm"];
  onDelete: Props["onDelete"];
  openEntity: OpenEntity;
  onOpen: (id: string) => void;
  subCount?: SubTask[];
}) {
  const done = task.status === "done";
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", task.id)}
      className="group cursor-grab rounded-2xl bg-ink-200/30 p-3.5 active:cursor-grabbing"
    >
      <div className="flex items-start gap-2">
        <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-ink-300" />
        <button
          onClick={() => api.actions.updateTask(task.id, { status: done ? "todo" : "done" })}
          className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 border-ink-300"
          style={done ? { background: "#10b981", borderColor: "#10b981" } : undefined}
          aria-label="Xong"
        >
          {done && <span className="text-[11px] text-white">✓</span>}
        </button>
        <button
          onClick={() => onOpen(task.id)}
          className={`flex-1 text-left text-[14px] font-semibold leading-snug ${done ? "text-ink-500 line-through" : ""}`}
        >
          {task.title}
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between pl-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-500">
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fullDate(task.dueDate)}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{task.estimateMinutes}m</span>
          {subCount && subCount.length > 0 && (
            <span>☑ {subCount.filter((s) => s.done).length}/{subCount.length}</span>
          )}
          {isRecurrenceParent(task) && (
            <span className="rounded-full bg-violet-100 px-1.5 text-[10px] font-semibold text-violet-600">
              🔁 {recurrenceLabel(task.recurrence!)}
            </span>
          )}
          {task.projectId && (
            <button onClick={() => openEntity("project", task.projectId!)} className="rounded bg-amber-100 px-1.5 text-amber-700">
              {api.state.projects.find((p) => p.id === task.projectId)?.name}
            </button>
          )}
          {task.goalId && (() => {
            const g = api.state.goals.find((g) => g.id === task.goalId);
            return g
              ? <button onClick={() => openEntity("goal", g.id)} className="rounded bg-teal-100 px-1.5 text-[10px] font-semibold text-teal-700">{g.title}</button>
              : null;
          })()}
          <span className="h-2 w-2 rounded-full" style={{ background: PRIO[task.priority] }} />
        </div>
        <RowActions onEdit={() => onOpen(task.id)} onDelete={() => onDelete(() => api.actions.remove("tasks", task.id))} />
      </div>
      {/* Click-to-move fallback — kéo-thả HTML5 hay lỗi (bôi đen chữ / cảm ứng);
          bấm tên cột để chuyển trạng thái chắc chắn. Cột hiện tại được tô đậm. */}
      <div className="mt-2 flex gap-1 pl-6" onClick={(e) => e.stopPropagation()}>
        {COLS.map(([s, colLabel]) => (
          <button
            key={s}
            onClick={() => api.actions.updateTask(task.id, { status: s })}
            disabled={task.status === s}
            aria-label={`Chuyển sang ${colLabel}`}
            className={`rounded-lg px-2 py-0.5 text-[10px] font-bold transition ${
              task.status === s ? "bg-brand-500 text-white" : "bg-ink-200/50 text-ink-500 hover:bg-ink-200"
            }`}
          >
            {colLabel}
          </button>
        ))}
      </div>
    </div>
  );
}
